import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('marketflow_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Templates API
export const templatesApi = {
  list: (params = {}) => api.get('/templates', { params }),
  get: (id) => api.get(`/templates/${id}`),
  generate: (data) => api.post('/templates/generate', data),
  render: (data) => api.post('/templates/render', data, { responseType: 'text' }),
  preview: (data) => api.post('/templates/preview', data),
};

// AI API
export const aiApi = {
  improveField: (data) => api.post('/ai/improve-field', data),
  improveDesign: (data) => api.post('/ai/improve-design', data),
  generateImage: (data) => api.post('/ai/generate-image', data, { timeout: 120000 }), // 2 min timeout for image gen
};

// n8n API
export const n8nApi = {
  getConfig: () => api.get('/automation/config'),
  updateConfig: (data) => api.put('/automation/config', data),
  generateText: (data) => api.post('/n8n/generate-text', data, { timeout: 60000 }),
  generateImage: (data) => api.post('/n8n/generate-image', data, { timeout: 120000 }),
  generateComplete: (data) => api.post('/n8n/generate-complete', data, { timeout: 180000 }),
};

// Credits API
export const creditsApi = {
  get: () => api.get('/credits'),
  add: (data) => api.post('/credits/add', data),
};

// Sectors API
export const sectorsApi = {
  list: () => api.get('/sectors'),
  create: (data) => api.post('/sectors', data),
  update: (id, data) => api.put(`/sectors/${id}`, data),
  delete: (id) => api.delete(`/sectors/${id}`),
};

// Message Types API
export const messageTypesApi = {
  list: () => api.get('/message-types'),
};

// Audiences API
export const audiencesApi = {
  list: () => api.get('/audiences'),
  create: (data) => api.post('/audiences', data),
  delete: (id) => api.delete(`/audiences/${id}`),
};

// Internal Contacts API (for local storage imports)
export const internalContactsApi = {
  list: (params = {}) => api.get('/contacts', { params }),
  delete: (id) => api.delete(`/contacts/${id}`),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/contacts/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  analyze: (sessionId) => api.post(`/contacts/analyze/${sessionId}`),
  import: (sessionId, options, audienceId) =>
    api.post(`/contacts/import/${sessionId}?audienceId=${audienceId || ''}`, options),
};

// Config API
export const configApi = {
  get: () => api.get('/config'),
  updateAI: (data) => api.post('/config/ai', data),
  testAIKey: (data) => api.post('/config/ai/test', data),
  updateMautic: (data) => api.post('/config/mautic', data),
  testMautic: () => api.post('/mautic/test-connection'),
  updateN8N: (data) => api.post('/config/automation', data),
};

// Mautic API
export const mauticApi = {
  testConnection: () => api.get('/mautic/test'),
  getSegments: () => api.get('/mautic/segments'),
  createSegment: (data) => api.post('/mautic/segments', data),
  updateSegment: (id, data) => api.put(`/mautic/segments/${id}`, data),
  deleteSegment: (id) => api.delete(`/mautic/segments/${id}`),
  getContacts: (params = {}) => api.get('/mautic/contacts', { params }),
  createContact: (data) => api.post('/mautic/contacts', data),
  updateContact: (id, data) => api.put(`/mautic/contacts/${id}`, data),
  deleteContact: (id) => api.delete(`/mautic/contacts/${id}`),
  addContactToSegment: (id, segmentId) => api.put(`/mautic/contacts/${id}/segment`, { segmentId }),
  getContactSegments: (id) => api.get(`/mautic/contacts/${id}/segments`),
  getEmails: (params = {}) => api.get('/mautic/emails', { params }),
  getEmailBlocks: (id) => api.get(`/mautic/emails/${id}/blocks`),
  getEmailPreview: (id) => api.get(`/mautic/emails/${id}/preview`),
  getCampaigns: () => api.get('/mautic/campaigns'),
  getStats: () => api.get('/mautic/stats'),
  sendEmail: (data) => api.post('/mautic/send-email', data),
};

// Company API
export const companyApi = {
  getMyCompany: () => api.get('/my-company'),
  updateMyCompany: (data) => api.put('/my-company', data),
  getClients: () => api.get('/clients/all'),
  getClient: (id) => api.get(`/clients/${id}`),
  updateClient: (id, data) => api.put(`/clients/${id}`, data),
};

export default api;
